import Subtitle from "../../text/Subtitle/Subtitle";
import Title from "../../text/Title/Title";
import { cn } from "@/utils/cnHelper";
import * as React from "react";

export type HeroProps = {
  title: string | React.ReactNode;
  description: string | React.ReactNode;
  catchline?: string | React.ReactNode;
  actions?: React.ReactNode;
  actionsBelowImage?: boolean;
  withBackgroundPattern?: boolean;
  image?: React.ReactNode;
  imagePlacement?: "right" | "left" | "bottom";
};

export default function Hero({
  title,
  description,
  catchline,
  image,
  actions,
  actionsBelowImage = true,
  withBackgroundPattern = false,
  imagePlacement = "right",
}: HeroProps) {
  return (
    <section className="tw-relative tw-isolate tw-overflow-hidden">
      {/* Background gradient and pattern */}
      {withBackgroundPattern && (
        <>
          <svg
            aria-hidden="true"
            className="tw-absolute tw-inset-0 tw--z-10 tw-size-full tw-stroke-brand_scale-500/30 [mask-image:radial-gradient(100%_100%_at_top_right,white,transparent)]"
          >
            <defs>
              <pattern x="50%" y={-1} id="983e3e4c-de6d-4c3f-8d64-b9761d1534cc" width={200} height={200} patternUnits="userSpaceOnUse">
                <path d="M.5 200V.5H200" fill="none" />
              </pattern>
            </defs>
            <rect fill="url(#983e3e4c-de6d-4c3f-8d64-b9761d1534cc)" width="100%" height="100%" strokeWidth={0} />
          </svg>
          <div
            aria-hidden="true"
            className="tw-absolute tw-left-[calc(50%-4rem)] tw-top-10 tw--z-10 tw-transform-gpu tw-blur-3xl sm:tw-left-[calc(50%-18rem)] lg:tw-left-48 lg:tw-top-[calc(50%-30rem)] xl:tw-left-[calc(50%-24rem)]"
          >
            <div
              style={{
                clipPath:
                  "polygon(73.6% 51.7%, 91.7% 11.8%, 100% 46.4%, 97.4% 82.2%, 92.5% 84.9%, 75.7% 64%, 55.3% 47.5%, 46.5% 49.4%, 45% 62.9%, 50.3% 87.2%, 21.3% 64.1%, 0.1% 100%, 5.4% 51.1%, 21.4% 63.9%, 58.9% 0.2%, 73.6% 51.7%)",
              }}
              className="tw-bg-gradient-to-r tw-aspect-[1108/632] tw-w-[69.25rem] tw-from-[#80caff] tw-to-[#4f46e5] tw-opacity-20"
            />
          </div>
        </>
      )}
      {/* Content */}
      <div
        className={cn(
          "tw-mx-auto tw-mt-10 tw-max-w-7xl tw-px-6 tw-pt-10 sm:tw-pt-16 lg:tw-px-8 lg:tw-pt-20",
          !image
            ? "lg:tw-flex lg:tw-justify-center"
            : imagePlacement === "bottom"
              ? "tw-flex tw-flex-col"
              : imagePlacement === "left"
                ? "lg:tw-flex lg:tw-flex-row-reverse lg:tw-items-center lg:tw-gap-16"
                : "lg:tw-flex lg:tw-items-center lg:tw-gap-16"
        )}
      >
        <div
          className={cn(
            "tw-mx-auto tw-shrink-0 lg:tw-pt-8",
            !image
              ? "tw-w-full lg:tw-mx-auto lg:tw-text-center"
              : imagePlacement === "bottom"
                ? "tw-w-full tw-text-center"
                : "tw-max-w-2xl tw-text-center lg:tw-mx-0 lg:tw-w-1/3 lg:tw-text-left"
          )}
        >
          {catchline && (
            <p className="!tw-mb-4 tw-text-sm tw-font-semibold tw-uppercase tw-tracking-[0.18em] tw-text-brand_scale-400">
              {catchline}
            </p>
          )}
          <Title as="h1">{title}</Title>
          <Subtitle variant="hero" className="tw-mt-8">
            {description}
          </Subtitle>
        </div>
        {actions && (
          <div
            className={cn(
              "tw-mt-6 tw-flex tw-flex-wrap tw-items-center tw-justify-center tw-gap-x-6 tw-gap-y-4",
              actionsBelowImage && "lg:tw-hidden"
            )}
          >
            {actions}
          </div>
        )}
        {image && (
          <div
            className={cn(
              "tw-mx-auto tw-w-full",
              imagePlacement === "bottom" ? "tw-mt-16 sm:tw-mt-24" : "tw-mt-16 sm:tw-mt-24 lg:tw-mt-0 lg:tw-w-2/3"
            )}
          >
            <div className="tw-w-full [&>*]:tw-h-auto [&>*]:tw-w-full [&>*]:tw-object-contain">{image}</div>
          </div>
        )}
      </div>
      {actions && actionsBelowImage && (
        <div className="tw-mx-auto tw-mt-16 tw-hidden tw-max-w-7xl tw-px-6 tw-pb-24 lg:tw-block lg:tw-px-8">
          <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-center tw-gap-x-6 tw-gap-y-4">{actions}</div>
        </div>
      )}
    </section>
  );
}
